import {downloadWorkbookWithFrozenRows} from '../../../../common/upload/excel.utils';
import {Component, ElementRef, EventEmitter, Input, OnDestroy, Output, ViewChild} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {firstValueFrom} from 'rxjs';
import * as XLSX from 'xlsx-js-style';
import {View} from '../../../../common/view';
import {AppContext} from '../../../../app-context';
import {AppCommonUtils} from '../../../../common/app-common-utils';
import {TranslateDirective} from '../../../../common/utils/translate.directive';
import {Entity} from '../../../../handlers/entity';
import {readFileAsBase64} from '../../locations/location-image.utils';
import {canUploadConnection, channelLabels, createUploadTemplate, PreparedUpload, prepareUpload, TemplateOptions, uploadChannels, uploadUnit} from './upload-workbook';
import {UploadPeriodRow} from './upload-history.types';

@Component({selector:'app-upload-workbook',standalone:true,imports:[CommonModule,FormsModule],
  templateUrl:'./upload-workbook.component.html',styleUrls:['./upload-history.component.scss']})
export class UploadWorkbookComponent extends View implements OnDestroy {
  @Input() connections:Entity[]=[];
  @Output() saved=new EventEmitter<number>();
  @Output() busyChange=new EventEmitter<boolean>();
  @ViewChild('workbookDialog',{static:true}) dialog:ElementRef<HTMLDialogElement>;
  mode:'template'|'upload'='template';
  connectionId='';
  options:TemplateOptions={year:new Date().getFullYear(),firstMonth:1,lastMonth:12,resolution:'month',channels:[]};
  error='';working=false;file?:File;prepared?:PreparedUpload;
  existing:UploadPeriodRow[]=[];overwriteApproved=false;
  private comparisons=new Map<string,{fingerprint:string;changed:number;added:number;unchanged:number}>();
  private before='';private alive=true;private revision=0;private returnFocus?:HTMLElement;
  months=Array.from({length:12},(_,i)=>i+1);
  t(key:string):string{return TranslateDirective.getTranslation(key.toLowerCase(),key) as string;}
  channel(type:string){return this.t(channelLabels[type]||type);}
  unit=uploadUnit;
  get candidates(){return this.mode==='upload'?this.connections.filter(e=>canUploadConnection(e,AppContext.userProfile)):this.connections;}
  get selected(){return this.candidates.find(e=>e.connection.connectionId===this.connectionId);}
  get channels(){return this.selected?uploadChannels(this.selected):[];}
  get overlaps(){return this.existing.filter(r=>!r.deleted&&(this.comparisons.get(r.periodId)?.changed||0)>0);}
  get deletedPeriods(){return this.existing.filter(r=>r.deleted);}
  month(month:number){return new Date(this.options.year,month-1,1,12).toLocaleDateString(AppCommonUtils.getPreferredLanguage()==='nl'?'nl-NL':'en-GB',{month:'long'});}
  number(value:number){return value.toLocaleString(AppCommonUtils.getPreferredLanguage()==='nl'?'nl-NL':'en-GB',{maximumFractionDigits:6});}
  title(){return this.t(this.mode==='template'?'Excel upload template':'Add upload');}
  open(mode:'template'|'upload',year:number,preferredConnectionId?:string){
    this.mode=mode;this.options.year=year;
    this.connectionId=this.candidates.some(e=>e.connection.connectionId===preferredConnectionId)?preferredConnectionId
      :this.candidates.length===1?this.candidates[0].connection.connectionId:'';
    this.resetFile();this.error='';this.selectConnection();this.returnFocus=document.activeElement as HTMLElement;
    this.dialog.nativeElement.showModal();
  }
  close(event?:Event){event?.preventDefault();if(this.working)return;this.revision++;this.dialog.nativeElement.close();this.resetFile();this.returnFocus?.focus({preventScroll:true});}
  ngOnDestroy(){this.alive=false;this.revision++;this.dialog.nativeElement.close();this.onDestroy();}
  resetFile(){this.prepared=undefined;this.file=undefined;this.existing=[];this.comparisons.clear();this.overwriteApproved=false;this.revision++;}
  selectConnection(){this.resetFile();this.options.channels=this.channels.slice();}
  toggleChannel(type:string,checked:boolean){this.options.channels=checked?[...this.options.channels,type]:this.options.channels.filter(t=>t!==type);}
  downloadTemplate(){
    this.error='';try{
      if(!this.selected)throw new Error('Choose a connection');
      const book=createUploadTemplate(this.selected,this.options,key=>this.t(key));
      downloadWorkbookWithFrozenRows(book,`flowmaps-${this.selected.connection.info.code}-${this.options.year}-template.xlsx`,new Map([['Data',1],['Info',1]]));
      this.close();
    }catch(error){this.error=this.t(error.message);}
  }
  async selectFile(input:HTMLInputElement){
    const file=input.files?.[0];input.value='';await this.reviewFile(file);
  }
  private async reviewFile(file?:File){
    this.resetFile();this.error='';if(!file)return;
    const revision=this.revision;this.setWorking(true);
    try{
      if(file.size>5_000_000)throw new Error('The source file must be smaller than 5 MB');
      const book=XLSX.read(await file.arrayBuffer(),{type:'array'});
      const prepared=prepareUpload(book,this.selected?[this.selected]:this.candidates,key=>this.t(key));
      const existing=await this.currentPeriods(prepared);
      const comparisons=await this.comparePeriods(prepared,existing);
      if(!this.alive||revision!==this.revision)return;
      this.file=file;this.prepared=prepared;this.existing=existing;this.comparisons=comparisons;this.before=this.signature(existing,comparisons);
    }catch(error){if(this.alive&&revision===this.revision)this.error=this.t(error.message);}
    finally{if(this.alive)this.setWorking(false);}
  }
  private setWorking(value:boolean){this.working=value;this.busyChange.emit(value);}
  async openExisting(file:File) {
    this.open('upload',new Date().getFullYear());
    this.connectionId='';
    await this.reviewFile(file);
  }
  private signature(rows:UploadPeriodRow[],comparisons=this.comparisons){return rows.map(r=>r.periodId+':'+r.revision+':'+r.deleted+':'+r.volume+':'+(comparisons.get(r.periodId)?.fingerprint||'')).sort().join('|');}
  private async comparePeriods(prepared:PreparedUpload,rows:UploadPeriodRow[]){
    const result=new Map<string,{fingerprint:string;changed:number;added:number;unchanged:number}>();
    for(const row of rows.filter(r=>!r.deleted&&r.pointCount>0)){
      const points=prepared.connectionData[row.connectionId]?.[row.dataType]?.filter(p=>p.timeRange.start.slice(0,7)===row.month)||[];
      if(points.length)result.set(row.periodId,await firstValueFrom(this.sendQuery('com.flowmaps.api.measurements.ReviewUploadedMeasurements',
        {sourceId:row.sourceId,dataType:row.dataType,month:row.month,points},{caching:false,showSpinner:false,hideError:true})));
    }
    return result;
  }
  private async currentPeriods(prepared:PreparedUpload):Promise<UploadPeriodRow[]>{
    const requests=new Map<string,{connectionId:string,sourceId?:string,year:number}>();
    prepared.periods.forEach(p=>{
      const connection=this.connections.find(e=>e.connection.connectionId===p.connectionId);
      const id=(p.dataType==='coolingConsumption'?'upload-cooling-':'upload-')+p.connectionId;
      const sourceId=connection?.connection.meters?.some(m=>m.meterId===id)?id:undefined;
      requests.set(id+p.month.slice(0,4),{connectionId:p.connectionId,sourceId,year:Number(p.month.slice(0,4))});
    });
    const rows:UploadPeriodRow[]=[];
    // One bounded source/year request at a time; no organisation-wide measurement scan.
    for(const request of requests.values()){
      const result=await firstValueFrom(this.sendQuery('com.flowmaps.api.measurements.uploads.GetUploadPeriods',request,{caching:false,showSpinner:false,hideError:true}));
      if(result.moreAvailable)throw new Error('Narrow the connection selection before uploading');
      for(const row of result.periods as UploadPeriodRow[])if(prepared.periods.some(p=>p.connectionId===row.connectionId&&p.dataType===row.dataType&&p.month===row.month))rows.push(row);
    }
    return [...new Map(rows.map(r=>[r.periodId,r])).values()];
  }
  private async waitForMeasurements(prepared:PreparedUpload):Promise<UploadPeriodRow[]|undefined>{
    for(let attempt=0;attempt<8&&this.alive;attempt++){
      try{
        const rows=await this.currentPeriods(prepared);
        if(prepared.periods.every(p=>rows.some(r=>!r.legacy&&r.connectionId===p.connectionId&&r.dataType===p.dataType&&r.month===p.month
            && r.revision > (this.existing.find(before=>before.periodId===r.periodId)?.revision ?? 0)))){
          const ready=await Promise.all(rows.map(row=>firstValueFrom(this.sendQuery('com.flowmaps.api.measurements.uploads.GetUploadPeriodReady',
            {periodId:row.periodId,revision:row.revision},{caching:false,showSpinner:false,hideError:true}))));
          if(ready.every(value=>value===true))return rows;
        }
      }catch{ /* The upload is saved; a temporarily unavailable projection must never resubmit it. */ }
      await new Promise(resolve=>setTimeout(resolve,Math.min(1000,200*(attempt+1))));
    }
    return undefined;
  }
  async import(){
    if(!this.prepared||!this.file||this.working||this.deletedPeriods.length||this.overlaps.length&&!this.overwriteApproved)return;
    this.error='';this.setWorking(true);
    try{
      if(Object.keys(this.prepared.connectionData).some(id=>!this.candidates.some(e=>e.connection.connectionId===id)))throw new Error('You cannot upload to this connection');
      const latest=await this.currentPeriods(this.prepared);
      const comparisons=await this.comparePeriods(this.prepared,latest);
      if(this.signature(latest,comparisons)!==this.before){this.existing=latest;this.comparisons=comparisons;this.before=this.signature(latest,comparisons);this.overwriteApproved=false;throw new Error('Measurements have changed. Review the upload again.');}
      const base64Data=await firstValueFrom(readFileAsBase64(this.file));
      await new Promise<void>((resolve,reject)=>this.sendCommand('com.flowmaps.api.measurements.UploadMeterData',{
        connectionData:this.prepared.connectionData,sourceName:this.file.name,
        approvals:Object.fromEntries(this.prepared.periods.map(period=>{
          const sourceId=(period.dataType==='coolingConsumption'?'upload-cooling-':'upload-')+period.connectionId;
          const id='uploaded-period:'+sourceId+':'+period.dataType+':'+period.month;
          return [id,{revision:latest.find(row=>row.periodId===id)?.revision||0,
            fingerprint:comparisons.get(id)?.fingerprint||'e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855',
            overwriteApproved:this.overwriteApproved}];
        })),
        sourceFile:{fileName:this.file.name,mediaType:this.file.type||'application/octet-stream',base64Data}
      },()=>resolve(),reject,{eventOnSuccess:false}));
      const ready=await this.waitForMeasurements(this.prepared);
      if(!this.alive)return;
      AppCommonUtils.clearCache();
      if(ready)await new Promise<void>((resolve,reject)=>this.sendCommand('com.flowmaps.api.measurements.uploads.NotifyUploadedMeasurementsReady',
        {periods:ready.map(row=>({periodId:row.periodId,revision:row.revision}))},()=>resolve(),reject,{eventOnSuccess:false}));
      const year=Number(this.prepared.periods[0].month.slice(0,4));
      this.setWorking(false);this.close();this.saved.emit(year);
      AppCommonUtils.registerSuccess(this.t(ready?'Upload processed':'Upload saved. Measurements are being updated; refresh shortly.'));
    }catch(error){this.error=this.t(error.message||'Upload could not be processed');}
    finally{if(this.alive)this.setWorking(false);}
  }
}
